import { createReducer, on } from '@ngrx/store';
import { LoadableStateReducerHelper } from '@shared/helpers/loadable-state-reducer.helper';
import { Loadable } from '@shared/interfaces/loadable.interface';
import * as DocumentOverviewActions from '../actions/document-overview.actions';
import * as DocumentRequestBatchActions from '../actions/document-request-batch.actions';
import { ClientDocumentGroup, DocumentOverviewBucket, DocumentOverviewModel } from '../models/document-overview.models';
import { GeneratedFilesResponse } from '../models/document-request-batch.models';

export const documentRequestBatchFeatureKey = 'documentRequestBatch';

export interface State {
  generatedFiles: Loadable<GeneratedFilesResponse | null>;
  clientGeneratedFiles: Loadable<GeneratedFilesResponse | null>;
  documentOverview: Loadable<DocumentOverviewModel | null>;
  clientDocumentOverview: Loadable<DocumentOverviewModel | null>;
}

export const initialState: State = {
  generatedFiles: {
    isLoading: false,
    data: null,
  },
  clientGeneratedFiles: {
    isLoading: false,
    data: null,
  },
  documentOverview: {
    isLoading: false,
    data: null,
  },
  clientDocumentOverview: {
    isLoading: false,
    data: null,
  },
};

export const reducer = createReducer<State>(
  initialState,
  on(DocumentRequestBatchActions.downloadGeneratedFiles, (state) => ({
    ...state,
    generatedFiles: LoadableStateReducerHelper.startLoad(initialState.generatedFiles),
  })),
  on(DocumentRequestBatchActions.downloadGeneratedFilesSuccess, (state, { response }) => ({
    ...state,
    generatedFiles: LoadableStateReducerHelper.loadSuccess(response),
  })),
  on(DocumentRequestBatchActions.downloadGeneratedFilesError, (state, { error }) => ({
    ...state,
    generatedFiles: LoadableStateReducerHelper.loadError(state.generatedFiles, error),
  })),
  on(DocumentRequestBatchActions.downloadClientGeneratedFiles, (state) => ({
    ...state,
    clientGeneratedFiles: LoadableStateReducerHelper.startLoad(initialState.clientGeneratedFiles),
  })),
  on(DocumentRequestBatchActions.downloadClientGeneratedFilesSuccess, (state, { response }) => ({
    ...state,
    clientGeneratedFiles: LoadableStateReducerHelper.loadSuccess(response),
  })),
  on(DocumentRequestBatchActions.downloadClientGeneratedFilesError, (state, { error }) => ({
    ...state,
    clientGeneratedFiles: LoadableStateReducerHelper.loadError(state.clientGeneratedFiles, error),
  })),
  on(DocumentOverviewActions.loadDocumentOverview, (state) => ({
    ...state,
    documentOverview: LoadableStateReducerHelper.startLoad(initialState.documentOverview),
  })),
  on(DocumentOverviewActions.loadDocumentOverviewSuccess, (state, { overview }) => ({
    ...state,
    documentOverview: LoadableStateReducerHelper.loadSuccess(overview),
  })),
  on(DocumentOverviewActions.loadDocumentOverviewError, (state, { error }) => ({
    ...state,
    documentOverview: LoadableStateReducerHelper.loadError(state.documentOverview, error),
  })),
  on(DocumentOverviewActions.loadClientDocumentOverview, (state) => ({
    ...state,
    clientDocumentOverview: LoadableStateReducerHelper.startLoad(state.clientDocumentOverview),
  })),
  on(DocumentOverviewActions.loadClientDocumentOverviewSuccess, (state, { overview }) => ({
    ...state,
    clientDocumentOverview: LoadableStateReducerHelper.loadSuccess(overview),
  })),
  on(DocumentOverviewActions.loadClientDocumentOverviewError, (state, { error }) => ({
    ...state,
    clientDocumentOverview: LoadableStateReducerHelper.loadError(state.clientDocumentOverview, error),
  })),
  on(DocumentOverviewActions.loadMoreClientDocuments, (state, { group }) => ({
    ...state,
    clientDocumentOverview: patchBucket(state.clientDocumentOverview, group, { isLoadingMore: true, loadMoreFailed: false }),
  })),
  on(DocumentOverviewActions.loadMoreClientDocumentsSuccess, (state, { group, bucket }) => ({
    ...state,
    clientDocumentOverview: appendToBucket(state.clientDocumentOverview, group, bucket),
  })),
  on(DocumentOverviewActions.loadMoreClientDocumentsError, (state, { group }) => ({
    ...state,
    clientDocumentOverview: patchBucket(state.clientDocumentOverview, group, { isLoadingMore: false, loadMoreFailed: true }),
  }))
);

function patchBucket(
  state: Loadable<DocumentOverviewModel | null>,
  group: ClientDocumentGroup,
  patch: Partial<DocumentOverviewBucket>
): Loadable<DocumentOverviewModel | null> {
  if (!state.data) {
    return state;
  }
  return {
    ...state,
    data: { ...state.data, [group]: { ...state.data[group], ...patch } },
  };
}

function appendToBucket(
  state: Loadable<DocumentOverviewModel | null>,
  group: ClientDocumentGroup,
  bucket: DocumentOverviewBucket
): Loadable<DocumentOverviewModel | null> {
  if (!state.data) {
    return state;
  }
  const existing = state.data[group];
  return {
    ...state,
    data: {
      ...state.data,
      [group]: { ...bucket, items: [...existing.items, ...bucket.items], isLoadingMore: false, loadMoreFailed: false },
    },
  };
}
